import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { keysFor } from './keymap';

export interface Command {
  id: string;
  title: string;
  run: () => void;
}

/** Case-insensitive substring filter over command titles. */
export function filterCommands(commands: Command[], query: string): Command[] {
  const q = query.trim().toLowerCase();
  if (!q) return commands;
  return commands.filter((c) => c.title.toLowerCase().includes(q));
}

interface Props { commands: Command[] }

const kbdStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 26, height: 26, padding: '0 7px',
  fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--color-text-muted)',
  background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: 6,
};

/**
 * ⌘P / Ctrl+P command palette — styleguide §07: large rows, shortcut chips,
 * substring filter, ↑/↓ select, Enter run, Esc close (returns focus to whatever
 * was focused when it opened). Chips come from the single-source keymap.
 */
export function CommandPalette({ commands }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const results = useMemo(() => filterCommands(commands, query), [commands, query]);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        setOpen((o) => {
          const next = !o;
          if (next) triggerRef.current = (document.activeElement as HTMLElement) ?? null;
          return next;
        });
        setQuery('');
        setIndex(0);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Esc / outside-click close → return focus to the element that had it.
  function close() {
    setOpen(false);
    triggerRef.current?.focus?.();
  }

  const clamped = Math.min(index, Math.max(0, results.length - 1));
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (open) itemRefs.current[clamped]?.scrollIntoView({ block: 'nearest' });
  }, [clamped, open]);

  if (!open) return null;
  const activeId = results[clamped] ? `cmdp-opt-${results[clamped].id}` : undefined;

  return (
    <>
      <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
      <div
        style={{
          position: 'fixed', left: '50%', top: '18%', transform: 'translateX(-50%)', zIndex: 200,
          width: 580, maxWidth: 'calc(100vw - 32px)',
          background: 'var(--color-menu-bg)', border: '1px solid var(--color-border)',
          borderRadius: 12, boxShadow: 'var(--shadow-2)', overflow: 'hidden',
          color: 'var(--color-text)', fontFamily: 'var(--font-mono)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* search row — 62px, lucide search + 18px input + esc chip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '0 20px', minHeight: 62, borderBottom: '1px solid var(--color-border)' }}>
          <Search width={20} height={20} style={{ color: 'var(--color-text-muted)', flex: 'none' }} aria-hidden />
          <input
            autoFocus
            role="combobox"
            aria-expanded
            aria-controls="cmdp-list"
            aria-label="Command palette"
            aria-activedescendant={activeId}
            value={query}
            placeholder="Type a command…"
            onChange={(e) => { setQuery(e.target.value); setIndex(0); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => Math.min(i + 1, results.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setIndex((i) => Math.max(i - 1, 0)); }
              else if (e.key === 'Enter') {
                e.preventDefault();
                const cmd = results[clamped];
                if (cmd) { setOpen(false); triggerRef.current?.focus?.(); cmd.run(); }
              } else if (e.key === 'Escape') { e.preventDefault(); close(); }
            }}
            style={{
              flex: 1, minWidth: 0, border: 'none', background: 'transparent',
              color: 'var(--color-text)', fontFamily: 'var(--font-mono)', fontSize: 18, outline: 'none',
            }}
          />
          <kbd style={kbdStyle}>esc</kbd>
        </div>

        {/* results — 52px rows, label + right-aligned shortcut chips */}
        <div id="cmdp-list" role="listbox" aria-label="Commands" style={{ padding: 8, maxHeight: 'min(58vh, 460px)', overflow: 'auto' }}>
          {results.map((c, i) => {
            const keys = keysFor(c.id);
            return (
              <div
                key={c.id}
                id={`cmdp-opt-${c.id}`}
                role="option"
                aria-selected={i === clamped}
                ref={(el) => { itemRefs.current[i] = el; }}
                onMouseEnter={() => setIndex(i)}
                onClick={() => { setOpen(false); triggerRef.current?.focus?.(); c.run(); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14, minHeight: 52,
                  padding: '0 12px', borderRadius: 8, cursor: 'pointer', fontSize: 16,
                  background: i === clamped ? 'color-mix(in oklab, var(--color-accent) 16%, transparent)' : 'transparent',
                }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
                {keys.length > 0 && (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 5, flex: 'none' }}>
                    {keys.map((k, ki) => <kbd key={ki} style={kbdStyle}>{k}</kbd>)}
                  </span>
                )}
              </div>
            );
          })}
          {results.length === 0 && (
            <div style={{ padding: '12px', color: 'var(--color-text-muted)', fontSize: 15 }}>No commands</div>
          )}
        </div>
      </div>
    </>
  );
}
